import React from 'react';
import type { Page, CurrentUser, Permission } from '../../types';
import HomeIcon from '../icons/HomeIcon';
import PointOfSaleIcon from '../icons/PointOfSaleIcon';
import ChefHatIcon from '../icons/ChefHatIcon';
import ClipboardListIcon from '../icons/ClipboardListIcon';
import SettingsIcon from '../icons/SettingsIcon';
import GiftIcon from '../icons/GiftIcon';
import ChevronDoubleLeftIcon from '../icons/ChevronDoubleLeftIcon';
import LogoutIcon from '../icons/LogoutIcon';
import LockClosedIcon from '../icons/LockClosedIcon';
import { useSoftKeyboard } from '../../contexts/SoftKeyboardContext';
import LiveDataStatusBadge from '../common/LiveDataStatusBadge';

interface SidebarProps {
  activePage: Page;
  setActivePage: (page: Page) => void;
  currentUser: CurrentUser;
  onLogout: () => void;
  isCollapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  onLockRegister?: () => void;
  isRegisterLocked?: boolean;
}

const navItems: { id: Page, name: string, icon: React.FC<any>, requiredPermission?: Permission }[] = [
  { id: 'dashboard', name: 'Dashboard', icon: HomeIcon },
  { id: 'pos', name: 'Point of Sale', icon: PointOfSaleIcon, requiredPermission: 'access_pos' },
  { id: 'pos-mobile', name: 'Mobile POS', icon: PointOfSaleIcon, requiredPermission: 'access_pos' },
  { id: 'kds', name: 'Kitchen Display', icon: ChefHatIcon },
  { id: 'service', name: 'Service Screen', icon: ClipboardListIcon },
];

const adminNavItems: { id: Page, name: string, icon: React.FC<any>, requiredPermission?: Permission }[] = [
    { id: 'coupon-campaigns', name: 'Coupon Campaigns', icon: GiftIcon, requiredPermission: 'manage_settings' },
    { id: 'settings', name: 'Settings', icon: SettingsIcon, requiredPermission: 'manage_settings' },
]

const Sidebar: React.FC<SidebarProps> = ({ activePage, setActivePage, currentUser, onLogout, isCollapsed, setCollapsed, onLockRegister, isRegisterLocked }) => {
  const userPermissions = currentUser.permissions;
  const { enabled: softKeyboardEnabled, toggleEnabled: toggleSoftKeyboard, closeKeyboard } = useSoftKeyboard();

  const NavButton: React.FC<{ item: typeof navItems[0] }> = ({ item }) => {
    if (item.requiredPermission && !userPermissions.has(item.requiredPermission)) {
        return null; // Don't render if user lacks permission
    }
    const Icon = item.icon;
    const handleNav = () => {
      closeKeyboard();
      setActivePage(item.id);
    };
    return (
        <li>
            <button
                type="button"
                onPointerDown={(e) => {
                  e.preventDefault();
                  handleNav();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav();
                }}
                className={`w-full flex items-center mb-1 sm:mb-2 rounded-md select-none ${
                activePage === item.id
                    ? 'bg-pink-600 text-white font-semibold'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white active:bg-gray-600'
                } ${isCollapsed ? 'justify-center py-2 sm:py-3' : 'px-2 sm:px-4 py-2 sm:py-3'}`}
                style={{ touchAction: 'manipulation' }}
                title={isCollapsed ? item.name : undefined}
            >
                <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${isCollapsed ? '' : 'mr-2 sm:mr-3'}`} />
                <span className={`${isCollapsed ? 'hidden' : ''} text-sm sm:text-base`}>{item.name}</span>
            </button>
        </li>
    );
  };

  return (
    <aside className={`bg-gray-800 text-white flex flex-col transition-all duration-300 ease-in-out ${isCollapsed ? 'w-16 sm:w-20' : 'w-56 sm:w-64'}`}>
      <div className="h-12 sm:h-16 flex items-center justify-center bg-gray-900 flex-shrink-0 overflow-hidden">
        <h1 className="text-lg sm:text-xl md:text-2xl font-bold whitespace-nowrap">{isCollapsed ? 'KP' : 'KINGS PARK'}</h1>
      </div>
      <nav className="flex-1 px-2 sm:px-4 py-3 sm:py-4 overflow-y-auto">
        <ul>
          {navItems.map((item) => <NavButton key={item.id} item={item} />)}
        </ul>
         <div className={`mt-4 pt-4 border-t border-gray-700 ${isCollapsed ? 'mx-[-8px]' : ''}`}>
             {!isCollapsed && <h3 className="px-4 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</h3>}
            <ul>
                {adminNavItems.map((item) => <NavButton key={item.id} item={item} />)}
            </ul>
        </div>
      </nav>
      <div className="px-4 py-4 border-t border-gray-700 flex-shrink-0">
         <button
              type="button"
              onClick={() => {
                closeKeyboard();
                setCollapsed(!isCollapsed);
              }}
              className="w-full flex items-center justify-center p-2 rounded-md text-gray-400 hover:bg-gray-700 hover:text-white mb-4"
              title={isCollapsed ? 'Expand Menu' : 'Collapse Menu'}
          >
              <ChevronDoubleLeftIcon className={`w-6 h-6 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
          </button>
         <div className={isCollapsed ? 'hidden' : 'text-left'}>
              <p className="font-semibold text-white truncate">{currentUser.name}</p>
              <p className="text-sm text-gray-400 capitalize">{currentUser.role}</p>
            <button
                type="button"
                onClick={() => {
                  closeKeyboard();
                  setActivePage('profile');
                }}
                className="w-full text-left mt-3 text-sm font-medium flex items-center gap-2 text-gray-300 hover:text-pink-400"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              My Profile
            </button>
            {onLockRegister && !isRegisterLocked && (
              <button
                  type="button"
                  onClick={() => {
                    closeKeyboard();
                    onLockRegister();
                  }}
                  className="w-full text-left mt-2 text-sm font-medium flex items-center gap-2 text-gray-300 hover:text-pink-400"
                  title="Lock register"
              >
                <LockClosedIcon className="w-4 h-4" />
                Lock Register
              </button>
            )}
            {isRegisterLocked && (
              <div className="w-full text-left mt-2 text-sm font-medium flex items-center gap-2 text-yellow-400">
                <LockClosedIcon className="w-4 h-4" />
                Register Locked
              </div>
            )}
            <button
                type="button"
                onClick={() => {
                  closeKeyboard();
                  onLogout();
                }}
                className="w-full text-left mt-2 text-sm text-gray-300 hover:text-pink-400 font-medium flex items-center gap-2"
            >
              <LogoutIcon className="w-4 h-4" />
              Logout
            </button>
            <div className="mt-4 mb-3">
              <LiveDataStatusBadge variant="inline" compact />
            </div>
            <div className="mt-4">
              <label className="text-sm text-gray-400 flex items-center justify-between">
                <span>Touch Keyboard</span>
                <button
                  type="button"
                  onClick={() => {
                    closeKeyboard();
                    toggleSoftKeyboard();
                  }}
                  className={`w-12 h-6 rounded-full transition-colors ${softKeyboardEnabled ? 'bg-pink-500' : 'bg-gray-600'}`}
                >
                  <span
                    className={`block w-5 h-5 bg-white rounded-full transform transition-transform ${softKeyboardEnabled ? 'translate-x-6' : 'translate-x-1'}`}
                  ></span>
                </button>
              </label>
            </div>
         </div>
         <div className={isCollapsed ? 'text-center space-y-2' : 'hidden'}>
            <div className="flex justify-center pb-1">
              <LiveDataStatusBadge variant="inline" compact />
            </div>
            {onLockRegister && !isRegisterLocked && (
              <button
                  type="button"
                  onClick={() => {
                    closeKeyboard();
                    onLockRegister();
                  }}
                  className="p-2 text-gray-400 hover:text-pink-400"
                  title="Lock register"
              >
                  <LockClosedIcon className="w-6 h-6" />
              </button>
            )}
            {isRegisterLocked && (
              <div className="p-2 text-yellow-400" title="Register is locked">
                  <LockClosedIcon className="w-6 h-6" />
              </div>
            )}
            <button
                type="button"
                onClick={() => {
                  closeKeyboard();
                  onLogout();
                }}
                className="p-2 text-gray-400 hover:text-pink-400"
                title="Logout"
            >
                <LogoutIcon className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={() => {
                closeKeyboard();
                toggleSoftKeyboard();
              }}
              className={`p-2 text-xs font-bold rounded-md border ${softKeyboardEnabled ? 'border-pink-400 text-pink-300' : 'border-gray-500 text-gray-400'}`}
              title="Toggle touch keyboard"
            >
              KB
            </button>
         </div>
      </div>
    </aside>
  );
};

export default Sidebar;
